import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { formatDate, formatDateTime } from '@/utils/format';

import type { ReportRecord, ReportDataEntry, ReportCallComment } from '@repo/shared-types';

// Extend jsPDF type to include autoTable
interface jsPDFWithAutoTable extends jsPDF {
  lastAutoTable: {
    finalY: number;
  };
}

const RATE_KEYS = new Set(['pickup_rate_all_calls', 'pickup_rate_excluding_under_22_seconds']);
const HOURS_KEYS = new Set([
  'total_hours_from_lead_generation_email',
  'total_hours_from_newcross_callbacks',
  'total_hours_captured_from_availability',
  'confirmed_hours_from_availability',
  'unconfirmed_hours_from_availability',
]);

function formatValue(value: number | null, key: string): string {
  if (value == null) return '-';
  if (RATE_KEYS.has(key)) return `${value.toFixed(2)}%`;
  if (HOURS_KEYS.has(key)) return value.toFixed(1);
  return value.toString();
}

/**
 * Group entries by category, preserving order of first appearance
 */
function groupByCategory(entries: ReportDataEntry[]): Map<string, ReportDataEntry[]> {
  const groups = new Map<string, ReportDataEntry[]>();
  for (const entry of entries) {
    const category = entry.category;
    if (!groups.has(category)) {
      groups.set(category, []);
    }
    groups.get(category)!.push(entry);
  }
  return groups;
}

export function generateReportPDF(report: ReportRecord, comments: ReportCallComment[] = []) {
  const doc = new jsPDF() as jsPDFWithAutoTable;
  const pageWidth = doc.internal.pageSize.getWidth();

  // Report Name
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(report.name, pageWidth / 2, 20, { align: 'center' });

  // Show campaign name OR date range
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  if (report.campaign_name) {
    doc.text(`Campaign: ${report.campaign_name}`, pageWidth / 2, 28, { align: 'center' });
  } else {
    const weekPeriod = `${formatDate(report.week_start_date)} - ${formatDate(report.week_end_date)}`;
    doc.text(weekPeriod, pageWidth / 2, 28, { align: 'center' });
  }

  // Generated date
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`Generated: ${formatDate(new Date())}`, pageWidth / 2, 35, { align: 'center' });

  let currentY = 45;

  // Group entries by category dynamically
  const categoryGroups = groupByCategory(report.data);

  // Check if any entry has a human reviewed value
  const hasHumanReview = report.data.some((e) => e.human_reviewed_value != null);

  // Render each category section
  for (const [category, entries] of categoryGroups) {
    // Build rows, skipping optional hours entries with no value
    const rows: string[][] = [];
    for (const entry of entries) {
      if (HOURS_KEYS.has(entry.key) && entry.value == null && entry.human_reviewed_value == null)
        continue;

      if (entry.key === 'error_percentage') continue;

      const row = [entry.label, formatValue(entry.value, entry.key)];
      if (hasHumanReview) {
        row.push(formatValue(entry.human_reviewed_value, entry.key));

        // Calculate error percentage based on human reviewed value (the correct value)
        if (
          entry.value !== null &&
          entry.human_reviewed_value !== null &&
          entry.human_reviewed_value !== 0
        ) {
          const diff = Math.abs(entry.human_reviewed_value - entry.value);
          const errorPct = (diff / Math.abs(entry.human_reviewed_value)) * 100;
          row.push(`${errorPct.toFixed(2)}%`);
        } else {
          row.push('-');
        }
      }
      rows.push(row);
    }

    if (rows.length === 0) continue;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text(category, 14, currentY);
    currentY += 5;

    const head = hasHumanReview
      ? [['Metric', 'Value', 'Human Reviewed', 'Error %']]
      : [['Metric', 'Value']];

    const columnStyles: Record<number, { cellWidth?: number | 'auto'; halign?: 'left' }> =
      hasHumanReview
        ? {
            0: { cellWidth: 85, halign: 'left' },
            1: { cellWidth: 30, halign: 'left' },
            2: { cellWidth: 35, halign: 'left' },
            3: { cellWidth: 'auto', halign: 'left' },
          }
        : {
            0: { cellWidth: 130, halign: 'left' },
            1: { cellWidth: 'auto', halign: 'left' },
          };

    autoTable(doc, {
      startY: currentY,
      head,
      body: rows,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246], halign: 'left' },
      styles: { fontSize: 10 },
      columnStyles,
    });

    currentY = doc.lastAutoTable.finalY + 15;
  }

  // Comments Section
  if (comments && comments.length > 0) {
    // Group comments by call_id and sort by date within each call
    const commentsByCall = comments.reduce(
      (acc, comment) => {
        if (!acc[comment.call_id]) {
          acc[comment.call_id] = [];
        }
        acc[comment.call_id].push(comment);
        return acc;
      },
      {} as Record<string, typeof comments>,
    );

    // Sort comments within each call by date (newest first)
    Object.values(commentsByCall).forEach((callComments) => {
      callComments.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    });

    // Add new page for comments section
    doc.addPage();
    currentY = 30;

    // Comments section title - centered and prominent
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text('Call Comments', pageWidth / 2, currentY, { align: 'center' });

    // Decorative line under title
    currentY += 5;
    doc.setDrawColor(59, 130, 246); // Blue color
    doc.setLineWidth(0.5);
    doc.line(pageWidth / 2 - 40, currentY, pageWidth / 2 + 40, currentY);
    currentY += 15;

    // Iterate through each call with comments
    for (const [callId, callComments] of Object.entries(commentsByCall)) {
      // Check if we need a new page for this call section
      if (currentY > doc.internal.pageSize.getHeight() - 80) {
        doc.addPage();
        currentY = 20;
      }

      // Get contact info from first comment (all comments for same call have same contact)
      const firstComment = callComments[0];
      const contactInfo = firstComment.contact_name
        ? `${firstComment.contact_name} (${firstComment.contact_phone || 'N/A'})`
        : 'Unknown Contact';

      // Call ID header (clickable link)
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(37, 99, 235); // Blue color
      const callIdText = `Call ID: ${callId}`;

      // Draw text and make it a link
      const textWidth = doc.getTextWidth(callIdText);
      doc.text(callIdText, 14, currentY);
      const callUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/calls/${callId}`;
      doc.link(14, currentY - 3, textWidth, 5, { url: callUrl });
      currentY += 6;

      // Contact info
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text(`Contact: ${contactInfo}`, 14, currentY);
      currentY += 6;

      // Comments for this call
      callComments.forEach((comment, _index) => {
        const commentDate = formatDateTime(comment.created_at);
        const userName = comment.user_name || 'Unknown User';

        // Check if we need a new page
        if (currentY > doc.internal.pageSize.getHeight() - 40) {
          doc.addPage();
          currentY = 20;
        }

        // Comment header (date and user)
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(`${userName} - ${commentDate}`, 20, currentY);
        currentY += 4;

        // Comment content
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(60);
        const splitContent = doc.splitTextToSize(comment.content, pageWidth - 30);
        doc.text(splitContent, 20, currentY);
        currentY += splitContent.length * 4 + 5;
      });

      // Add space between calls
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.5);
      doc.line(14, currentY + 2, pageWidth - 10, currentY + 2);
      currentY += 8;
    }
  }

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, {
      align: 'center',
    });
  }

  // Download PDF
  const safeName = report.name.replace(/[^a-z0-9]/gi, '_');
  const filename = `${safeName}_${formatDate(report.week_start_date).replace(/\s/g, '_')}.pdf`;
  doc.save(filename);
}
